import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import AgendaCalendar from "@/components/agenda/AgendaCalendar";
import AgendaDiaView from "@/components/agenda/AgendaDiaView";
import AgendaModal from "@/components/agenda/AgendaModal";
import { useLojas } from "@/hooks/useLojas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESES = [
  "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const Agenda = () => {
  const [mesAtual, setMesAtual] = useState(new Date().getMonth());
  const [anoAtual, setAnoAtual] = useState(new Date().getFullYear());
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const lojas = useLojas();

  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [horariosDia, setHorariosDia] = useState([]);
  const [loadingDia, setLoadingDia] = useState(false);

  const [modalAberto, setModalAberto] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [agendamentoSelecionado, setAgendamentoSelecionado] = useState(null);
  const [formData, setFormData] = useState({
    horario: "", loja: "", nome: "", telefone: "", idade: "", observacoes: "",
  });

  const carregarAgendamentos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/agenda`, {
        params: { mes: mesAtual + 1, ano: anoAtual },
      });
      setAgendamentos(res.data.agendamentos || []);
    } catch {
      toast.error("Erro ao carregar agendamentos");
    } finally {
      setLoading(false);
    }
  }, [mesAtual, anoAtual]);

  useEffect(() => {
    carregarAgendamentos();
  }, [carregarAgendamentos]);

  const carregarHorariosDoDia = async (data) => {
    try {
      setLoadingDia(true);
      const res = await axios.get(`${API}/agenda/dia/${data}`);
      setHorariosDia(res.data.horarios || []);
    } catch {
      toast.error("Erro ao carregar horarios do dia");
      setHorariosDia([]);
    } finally {
      setLoadingDia(false);
    }
  };

  const getDiasDoMes = () => {
    const primeiroDia = new Date(anoAtual, mesAtual, 1);
    const ultimoDia = new Date(anoAtual, mesAtual + 1, 0);
    const diasNoMes = ultimoDia.getDate();
    const diaSemanaInicio = primeiroDia.getDay();

    const dias = [];
    for (let i = 0; i < diaSemanaInicio; i++) {
      dias.push({ dia: null, data: null });
    }
    for (let dia = 1; dia <= diasNoMes; dia++) {
      const dataStr = `${anoAtual}-${String(mesAtual + 1).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
      dias.push({ dia, data: dataStr });
    }
    return dias;
  };

  const getAgendamentosDoDia = (data) => {
    if (!data) return [];
    return agendamentos.filter((ag) => ag.data === data).sort((a, b) => a.horario.localeCompare(b.horario));
  };

  const abrirDia = async (data) => {
    setDiaSelecionado(data);
    await carregarHorariosDoDia(data);
  };

  const voltarParaCalendario = () => {
    setDiaSelecionado(null);
    setHorariosDia([]);
    carregarAgendamentos();
  };

  const abrirModalNovoAgendamento = (horario, loja) => {
    setModoEdicao(false);
    setAgendamentoSelecionado(null);
    setFormData({ horario, loja, nome: "", telefone: "", idade: "", observacoes: "" });
    setModalAberto(true);
  };

  const abrirModalEdicao = (agendamento) => {
    setModoEdicao(true);
    setAgendamentoSelecionado(agendamento);
    setFormData({
      horario: agendamento.horario,
      loja: agendamento.loja,
      nome: agendamento.nome || "",
      telefone: agendamento.telefone || "",
      idade: agendamento.idade?.toString() || "",
      observacoes: agendamento.observacoes || "",
    });
    setModalAberto(true);
  };

  const salvarAgendamento = async () => {
    if (!formData.horario || !formData.loja) {
      return toast.error("Selecione horario e loja");
    }
    try {
      const dados = {
        data: diaSelecionado,
        horario: formData.horario,
        loja: formData.loja,
        nome: formData.nome || null,
        telefone: formData.telefone || null,
        idade: formData.idade ? parseInt(formData.idade) : null,
        observacoes: formData.observacoes || null,
      };
      if (modoEdicao && agendamentoSelecionado) {
        await axios.put(`${API}/agenda/${agendamentoSelecionado.id}`, dados);
        toast.success("Agendamento atualizado!");
      } else {
        await axios.post(`${API}/agenda`, dados);
        toast.success("Agendamento criado!");
      }
      setModalAberto(false);
      await carregarHorariosDoDia(diaSelecionado);
      carregarAgendamentos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar agendamento");
    }
  };

  const excluirAgendamento = async (id) => {
    if (!window.confirm("Deseja realmente excluir este agendamento?")) return;
    try {
      await axios.delete(`${API}/agenda/${id}`);
      toast.success("Agendamento excluido!");
      await carregarHorariosDoDia(diaSelecionado);
      carregarAgendamentos();
    } catch {
      toast.error("Erro ao excluir agendamento");
    }
  };

  const exportarExcel = async () => {
    try {
      const res = await axios.get(`${API}/agenda/exportar/excel`, {
        params: { mes: mesAtual + 1, ano: anoAtual },
      });
      const dados = res.data.dados;
      if (!dados || dados.length === 0) {
        return toast.error("Nenhum agendamento para exportar");
      }
      const headers = ["Data", "Horario", "Loja", "Nome", "Telefone", "Idade", "Observacoes"];
      const rows = dados.map((item) => [
        item.Data || "", item.Horario || "", item.Loja || "", item.Nome || "",
        item.Telefone || "", item.Idade || "", item.Observacoes || "",
      ]);
      const csvContent = [
        headers.join(";"),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(";")),
      ].join("\n");
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `agenda_${MESES[mesAtual].toLowerCase()}_${anoAtual}.csv`;
      link.click();
      toast.success("Agenda exportada com sucesso!");
    } catch {
      toast.error("Erro ao exportar agenda");
    }
  };

  const navegarMes = (direcao) => {
    if (direcao === -1) {
      if (mesAtual === 0) {
        setMesAtual(11);
        setAnoAtual(anoAtual - 1);
      } else {
        setMesAtual(mesAtual - 1);
      }
    } else if (mesAtual === 11) {
      setMesAtual(0);
      setAnoAtual(anoAtual + 1);
    } else {
      setMesAtual(mesAtual + 1);
    }
  };

  const isHoje = (data) => {
    if (!data) return false;
    const hoje = new Date().toISOString().split("T")[0];
    return data === hoje;
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return "";
    const date = new Date(dataStr + "T12:00:00");
    return date.toLocaleDateString("pt-BR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  };

  if (diaSelecionado) {
    return (
      <>
        <AgendaDiaView
          diaSelecionado={diaSelecionado}
          horariosDia={horariosDia}
          loadingDia={loadingDia}
          lojas={lojas}
          formatarData={formatarData}
          voltarParaCalendario={voltarParaCalendario}
          abrirModalNovoAgendamento={abrirModalNovoAgendamento}
          abrirModalEdicao={abrirModalEdicao}
          excluirAgendamento={excluirAgendamento}
        />
        <AgendaModal
          open={modalAberto}
          onOpenChange={setModalAberto}
          modoEdicao={modoEdicao}
          formData={formData}
          setFormData={setFormData}
          onSalvar={salvarAgendamento}
        />
      </>
    );
  }

  return (
    <AgendaCalendar
      mesAtual={mesAtual}
      anoAtual={anoAtual}
      diasDoMes={getDiasDoMes()}
      loading={loading}
      navegarMes={navegarMes}
      exportarExcel={exportarExcel}
      getAgendamentosDoDia={getAgendamentosDoDia}
      isHoje={isHoje}
      abrirDia={abrirDia}
    />
  );
};

export default Agenda;
